import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileText, Layers, ScrollText } from "lucide-react";
import { CEmblem } from "@/components/Brand";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { useSEO } from "@/lib/seo";

const FORMATS = [
  {
    icon: FileText,
    tag: "Article",
    text: "Des contenus courts pour expliquer une actualité, une décision, un document ou une évolution concrète.",
  },
  {
    icon: ScrollText,
    tag: "Analyse",
    text: "Des textes plus développés pour confronter les discours publics aux éléments disponibles.",
  },
  {
    icon: Layers,
    tag: "Dossier",
    text: "Des publications approfondies réunissant une analyse, une chronologie, des sources et les réponses éventuellement reçues.",
  },
];

// Composition graphique : deux cadres confrontés (une representation soumise a une seconde lecture).
function HeroComposition() {
  return (
    <div aria-hidden className="relative w-full aspect-square max-w-[220px] sm:max-w-[320px] lg:max-w-[440px] mx-auto">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="grid grid-cols-6 grid-rows-6 gap-2 w-[72%] h-[72%] opacity-[0.14]">
          {Array.from({ length: 36 }).map((_, i) => (
            <span key={i} className="bg-paper rounded-[2px]" />
          ))}
        </div>
      </div>
      <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" fill="none">
        <rect x="24" y="34" width="112" height="112" rx="12" stroke="#FF4D1C" strokeWidth="16" />
        <rect x="70" y="66" width="112" height="112" rx="12" stroke="#F7F5F0" strokeWidth="16" />
      </svg>
      <span className="absolute left-[10%] top-[12%] font-display font-black text-orange text-sm uppercase tracking-widest">
        v.1
      </span>
    </div>
  );
}

const Btn = ({ to, children, variant = "primary", testid }) => {
  const base =
    "inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wide px-6 py-3.5 rounded-[10px] transition-colors duration-200";
  const styles = {
    primary: "bg-orange text-ink hover:bg-paper hover:text-ink",
    ghost: "border-2 border-paper text-paper hover:bg-paper hover:text-ink",
    inkPrimary: "bg-ink text-paper hover:bg-white hover:text-ink",
    inkGhost: "border-2 border-ink text-ink hover:bg-ink hover:text-paper",
  };
  return (
    <Link to={to} data-testid={testid} className={`${base} ${styles[variant]}`}>
      {children}
    </Link>
  );
};

export default function Home() {
  useSEO({
    title: null,
    description:
      "Contre-épreuve, projet éditorial indépendant consacré aux faits, aux sources et aux récits publics. Les premières publications arrivent progressivement.",
  });

  return (
    <div>
      {/* SECTION 1 : OUVERTURE */}
      <section className="bg-ink text-paper relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div>
            <p className="text-orange font-bold text-sm uppercase tracking-wide mb-6">
              Projet éditorial indépendant
            </p>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] uppercase">
              Un récit constitue une première version.{" "}
              <span className="text-orange">Nous en cherchons la contre-épreuve.</span>
            </h1>
            <p className="mt-7 text-lg text-paper/75 leading-relaxed max-w-xl">
              Contre-épreuve est un projet éditorial indépendant consacré aux faits, aux sources et aux
              récits publics. Les premières publications seront mises en ligne progressivement. Elles
              prendront la forme d'articles, de vérifications et de dossiers documentés.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Btn to="/a-propos" variant="primary" testid="hero-decouvrir-btn">
                Découvrir le projet <ArrowRight size={17} />
              </Btn>
              <Btn to="/contribuer" variant="ghost" testid="hero-proposer-btn">
                Proposer un article <ArrowUpRight size={17} />
              </Btn>
            </div>
          </div>
          <HeroComposition />
        </div>
      </section>

      {/* SECTION 2 : CE QUE CONTRE-ÉPREUVE PUBLIERA */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24">
        <h2 className="font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] text-ink max-w-3xl">
          Des articles aux dossiers documentés
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {FORMATS.map(({ icon: Icon, tag, text }, i) => (
            <div
              key={tag}
              data-testid={`format-card-${tag.toLowerCase()}`}
              className={`rounded-[18px] p-8 border-2 ${
                i === 1 ? "bg-ink text-paper border-ink" : "bg-white border-line text-ink"
              }`}
            >
              <span
                className={`inline-flex h-12 w-12 items-center justify-center rounded-[12px] ${
                  i === 1 ? "bg-orange text-ink" : "bg-highlight text-ink"
                }`}
              >
                <Icon size={22} strokeWidth={2.2} />
              </span>
              <h3 className="mt-6 font-display font-black text-2xl uppercase leading-none">{tag}</h3>
              <p className={`mt-3 leading-relaxed ${i === 1 ? "text-paper/75" : "text-muted-ink"}`}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 3 : LES SUJETS ABORDÉS */}
      <section id="sujets" className="bg-paper border-t border-line scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-20">
          <h2 className="font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] text-ink">
            Les sujets abordés
          </h2>
          <p className="mt-5 text-lg text-muted-ink leading-relaxed max-w-3xl">
            Contre-épreuve s'intéressera notamment aux organisations, aux institutions, aux
            transformations numériques, aux questions économiques et sociales, aux dispositifs publics
            et aux récits qui accompagnent leur action.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            {["Organisations", "Institutions", "Numérique", "Économie", "Société", "Vie quotidienne"].map((theme, i) => {
              const accent = i % 2 === 1;
              return (
                <span
                  key={theme}
                  data-testid={`sujet-${theme.toLowerCase().replace(/\s/g, "-")}`}
                  className={`inline-flex items-center gap-2.5 rounded-[12px] border-2 px-5 py-3 font-display font-black uppercase text-base md:text-lg ${
                    accent ? "bg-ink text-paper border-ink" : "bg-white text-ink border-ink"
                  }`}
                >
                  <span className="h-2 w-2 rounded-[2px] bg-orange" aria-hidden="true" />
                  {theme}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 : POURQUOI CE NOM ? */}
      <section className="bg-white border-y border-line">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-start">
          <div>
            <p className="text-orange font-bold text-sm uppercase tracking-wide mb-4">Le sens du nom</p>
            <h2 className="font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] text-ink">
              Pourquoi Contre-épreuve ?
            </h2>
          </div>
          <div>
            <div className="prose-editorial text-lg">
              <p>
                Une contre-épreuve est une seconde vérification. Elle consiste à reprendre une première
                représentation, à la confronter à d'autres éléments et à observer ce qui résiste, ce qui
                se nuance et ce qui se contredit.
              </p>
              <p>
                Dans le contexte du média, la contre-épreuve consiste à soumettre un récit public à un
                nouvel examen, en le confrontant aux faits, aux sources et aux éléments qui peuvent le
                confirmer, le préciser ou le remettre en question.
              </p>
            </div>
            <div className="mt-8 bg-highlight rounded-[16px] p-8">
              <p className="font-display font-black text-2xl md:text-3xl uppercase leading-[1.05] text-ink">
                Le but n'est pas de prouver que le premier récit est faux. Il est de déterminer ce qu'il
                permet réellement d'affirmer.
              </p>
            </div>
            <Link
              to="/a-propos"
              data-testid="home-ensavoirplus-btn"
              className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ink hover:text-orange transition-colors duration-200"
            >
              En savoir plus <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 4 : CONTRIBUER */}
      <section className="bg-orange text-ink">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <div>
            <h2 className="font-display font-black text-3xl md:text-5xl uppercase leading-[0.95]">
              Une information ou un article à transmettre ?
            </h2>
            <p className="mt-6 text-ink/85 leading-relaxed max-w-2xl text-lg">
              Contre-épreuve peut recevoir des propositions d'articles, des analyses documentées, des
              documents ou des informations susceptibles de nourrir une publication. Les propositions
              sont examinées avant toute décision de publication.
            </p>
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <Btn to="/contribuer?type=article" variant="inkPrimary" testid="home-propose-btn">
              Proposer un article <ArrowUpRight size={18} />
            </Btn>
            <Link
              to="/contribuer?type=information"
              data-testid="home-transmit-btn"
              className="inline-flex items-center justify-center gap-2 border-2 border-ink text-ink font-bold uppercase tracking-wide px-6 py-3.5 rounded-[10px] hover:bg-ink hover:text-orange transition-colors duration-200"
            >
              Transmettre une information <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 5 : PREMIÈRES PUBLICATIONS */}
      <section className="bg-ink text-paper relative overflow-hidden">
        <div aria-hidden className="pointer-events-none select-none absolute right-[-6%] bottom-[-14%] w-[46%] max-w-[520px] opacity-[0.06]">
          <CEmblem className="w-full h-auto" primary="#F7F5F0" secondary="#F7F5F0" />
        </div>
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 relative z-10">
          <p className="text-orange font-bold text-sm uppercase tracking-wide">Lancement à l'automne 2026</p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] max-w-4xl">
            Les premières publications arrivent progressivement
          </h2>
          <p className="mt-6 text-paper/75 leading-relaxed max-w-2xl text-lg">
            Contre-épreuve commencera par publier des articles courts consacrés à des sujets pratiques,
            institutionnels, économiques et numériques. Des analyses plus substantielles et des dossiers
            documentés viendront ensuite compléter le site.
          </p>
          <NewsletterSignup />
        </div>
      </section>
    </div>
  );
}
